import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/ui-helpers";
import { Plus, ChevronLeft, ChevronRight, X, Paperclip, Trash2, Receipt, Pencil, Eye, ShieldAlert, Camera, Sparkles, Loader2 } from "lucide-react";
import { money, monthKey, prettyMonth, prettyDate, todayISO } from "@/mobile/mobileUtils";
import { toast } from "sonner";

const CAT_COLORS = ["#1479C4", "#F5822A", "#5A34B4", "#0EA5A5", "#1E9BE9", "#EAB308", "#7C3AED", "#0891B2", "#DB2777", "#16A34A", "#64748B"];

function shiftMonth(key, delta) {
  let [y, m] = key.split("-").map(Number);
  m += delta;
  while (m <= 0) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

export default function MobileExpenses() {
  const { user } = useAuth();
  const [month, setMonth] = useState(monthKey());
  const [dash, setDash] = useState(null);
  const [list, setList] = useState(null);
  const [sheet, setSheet] = useState(null); // null | {} (new) | expense (edit)

  const load = () => {
    setDash(null); setList(null);
    api.get(`/expenses-dashboard?month=${month}`).then((r) => setDash(r.data)).catch(() => setDash({ total: 0, count: 0, by_category: [], trend: [] }));
    api.get(`/expenses?month=${month}`).then((r) => setList(r.data || [])).catch(() => setList([]));
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [month]); // eslint-disable-line

  if (user && user.role !== "admin") {
    return (
      <div className="p-6 text-center text-slate-400" data-testid="exp-admin-only">
        <ShieldAlert className="h-8 w-8 mx-auto mb-2" />
        <div className="text-sm">Expenses are available to administrators only.</div>
      </div>
    );
  }

  const del = async (e) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Expense deleted"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const viewReceipt = async (e) => {
    try {
      const res = await api.get(`/expenses/${e.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open receipt"); }
  };

  const maxCat = dash?.by_category?.[0]?.amount || 1;
  const maxTrend = Math.max(1, ...(dash?.trend || []).map((t) => t.amount));

  return (
    <div className="p-4 space-y-4 relative" data-testid="mobile-expenses">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-xl text-slate-900">Expenses</h1>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl">
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="exp-month-prev" className="p-2 text-slate-500"><ChevronLeft className="h-4 w-4" /></button>
          <div className="text-xs font-semibold text-slate-700 w-28 text-center" data-testid="exp-month-label">{prettyMonth(month)}</div>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="exp-month-next" className="p-2 text-slate-500"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Dashboard */}
      {!dash ? <Loader /> : (
        <div className="bg-gradient-to-br from-secondary to-[#123a63] text-white rounded-2xl p-4" data-testid="exp-dashboard">
          <div className="text-xs text-slate-300">Total · {prettyMonth(month)}</div>
          <div className="font-head font-extrabold text-3xl mt-1">{money(dash.total, dash.currency)}</div>
          <div className="text-xs text-slate-300">{dash.count} expense{dash.count === 1 ? "" : "s"}</div>

          {dash.trend?.length > 0 && (
            <div className="flex items-end gap-1.5 h-14 mt-4">
              {dash.trend.map((t) => (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t bg-[hsl(var(--brand-blue))]" style={{ height: `${Math.max(4, (t.amount / maxTrend) * 44)}px`, opacity: t.month === month ? 1 : 0.5 }} title={money(t.amount)} />
                  <div className="text-[8px] text-slate-300">{t.month.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {dash?.by_category?.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-2.5" data-testid="exp-by-category">
          <div className="text-sm font-head font-bold text-slate-800">By category</div>
          {dash.by_category.map((c, i) => (
            <div key={c.category}>
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{c.category}</span><span className="font-semibold text-slate-800">{money(c.amount)}</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.amount / maxCat) * 100}%`, background: CAT_COLORS[i % CAT_COLORS.length] }} /></div>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {list === null ? null : list.length === 0 ? (
        <div className="text-center py-10 text-slate-400"><Receipt className="h-8 w-8 mx-auto mb-2" /><div className="text-sm">No expenses in {prettyMonth(month)}.</div></div>
      ) : (
        <div className="space-y-2" data-testid="exp-list">
          {list.map((e) => (
            <div key={e.id} className="bg-white rounded-2xl p-3.5 border border-slate-100" data-testid={`exp-row-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-sm">{e.category}</div>
                  <div className="text-[11px] text-slate-400">{prettyDate(e.date)}{e.staff_name ? ` · ${e.staff_name}` : ""}{e.client_name ? ` · ${e.client_name}` : ""}</div>
                  {e.note && <div className="text-xs text-slate-500 mt-0.5">{e.note}</div>}
                </div>
                <div className="font-head font-extrabold text-slate-900 shrink-0">{money(e.amount, e.currency)}</div>
              </div>
              <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-slate-50">
                {e.has_receipt && <button onClick={() => viewReceipt(e)} data-testid={`exp-view-receipt-${e.id}`} className="text-xs text-primary font-semibold inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Receipt</button>}
                <button onClick={() => setSheet(e)} data-testid={`exp-edit-${e.id}`} className="text-xs text-slate-500 font-semibold inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => del(e)} data-testid={`exp-delete-${e.id}`} className="text-xs text-rose-500 font-semibold inline-flex items-center gap-1 ml-auto"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
      <button onClick={() => setSheet({})} data-testid="exp-add-fab" className="fixed bottom-24 right-4 h-14 w-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform z-20"><Plus className="h-6 w-6" /></button>

      {sheet && <ExpenseSheet expense={sheet.id ? sheet : null} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); load(); }} />}
    </div>
  );
}

function ExpenseSheet({ expense, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!expense;
  const [cats, setCats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState(isEdit
    ? { category: expense.category, amount: String(expense.amount), date: expense.date, staff_id: expense.staff_id || "", note: expense.note || "", client_id: expense.client_id || "", receipt_file_id: expense.receipt_file_id || null, receipt_filename: expense.receipt_filename || null }
    : { category: "", amount: "", date: todayISO(), staff_id: "", note: "", client_id: "", receipt_file_id: null, receipt_filename: null });
  const [clientName, setClientName] = useState(expense?.client_name || "");
  const [cq, setCq] = useState("");
  const [cresults, setCresults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);     // { merchant, confidence, filled: [...] } after a successful read
  const [preview, setPreview] = useState(null); // local object URL of the snapped photo
  const [scanError, setScanError] = useState(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  useEffect(() => {
    api.get("/expense-categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/team/staff").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!cq) { setCresults([]); return; }
    const t = setTimeout(() => api.get(`/clients?q=${encodeURIComponent(cq)}`).then((r) => setCresults((r.data || []).slice(0, 6))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [cq]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/expenses/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, receipt_file_id: data.file_id, receipt_filename: data.filename }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const scanPhoto = async (file) => {
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setScan(null); setScanError(null); setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/expenses/receipt/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = data.suggestion;
      setF((prev) => {
        const next = { ...prev, receipt_file_id: data.file_id, receipt_filename: data.filename };
        if (!s) return next;
        if (s.amount) next.amount = String(s.amount);
        if (s.category) next.category = s.category;
        if (s.date) next.date = s.date;
        if (s.currency) next.currency = s.currency;
        if (!prev.note && (s.merchant || s.note)) next.note = [s.merchant, s.note].filter(Boolean).join(" — ").slice(0, 120);
        return next;
      });
      if (s) {
        const filled = ["amount", "category", "date"].filter((k) => s[k]);
        setScan({ merchant: s.merchant, confidence: s.confidence, filled });
        toast.success("Receipt read — check the details below");
      } else {
        setScanError(data.error || "Couldn't read the receipt. Fill in the details manually.");
      }
    } catch (e) {
      setScanError(apiErr(e));
      toast.error(apiErr(e));
    } finally { setScanning(false); }
  };

  const save = async () => {
    if (!f.category) { toast.error("Choose a category"); return; }
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    const payload = { ...f, amount: amt, client_id: f.client_id || null, staff_id: f.staff_id || null };
    try {
      if (isEdit) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(isEdit ? "Expense updated" : "Expense added");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end" onClick={onClose}>
      <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="exp-sheet" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-extrabold text-lg text-slate-900">{isEdit ? "Edit expense" : "New expense"}</h3>
          <button onClick={onClose} data-testid="exp-sheet-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <div className="space-y-3">
          {/* Snap a receipt → AI fills the fields below for review */}
          {user?.ai_enabled && <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/[0.04] p-3" data-testid="exp-scan-block">
            <label data-testid="exp-scan-label" className="flex items-center gap-3 cursor-pointer active:opacity-80">
              {preview ? (
                <img src={preview} alt="Receipt" data-testid="exp-scan-preview" className="h-16 w-16 rounded-xl object-cover border border-slate-200 shrink-0" />
              ) : (
                <span className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Camera className="h-6 w-6 text-primary" /></span>
              )}
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">{scanning ? "Reading receipt…" : preview ? "Retake photo" : "Snap the receipt"}</span>
                <span className="block text-[11px] text-slate-500 leading-snug">
                  {scanning ? "Extracting amount, date and category" : "The photo is attached and the fields below are filled in for you"}
                </span>
              </span>
              {scanning && <Loader2 className="h-4 w-4 text-primary animate-spin ml-auto shrink-0" />}
              <input data-testid="exp-scan-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => scanPhoto(e.target.files?.[0])} />
            </label>
            {scan && (
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="exp-scan-result">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-2 py-1 rounded-full"><Sparkles className="h-3 w-3" /> AI filled</span>
                {scan.filled.map((k) => <span key={k} className="text-[10px] font-semibold text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-full capitalize">{k}</span>)}
                {scan.merchant && <span className="text-[11px] text-slate-500 truncate">{scan.merchant}</span>}
              </div>
            )}
            {scanError && <div className="mt-2 text-[11px] text-amber-600" data-testid="exp-scan-error">{scanError}</div>}
          </div>}

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Amount (CAD)</label>
            <input data-testid="exp-amount" type="number" inputMode="decimal" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" className="mt-1 w-full px-3.5 py-3 rounded-xl border border-slate-200 text-lg font-head font-bold focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Category</label>
            <select data-testid="exp-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm bg-white">
              <option value="">Select category…</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Date</label>
            <input data-testid="exp-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1 w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Staff (optional)</label>
            <select data-testid="exp-staff" value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} className="mt-1 w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm bg-white">
              <option value="">Not linked</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Client (optional)</label>
            {f.client_id ? (
              <div className="mt-1 flex items-center justify-between px-3.5 py-3 rounded-xl border border-slate-200 bg-slate-50">
                <span className="text-sm font-medium text-slate-800">{clientName || "Selected client"}</span>
                <button onClick={() => { setF({ ...f, client_id: "" }); setClientName(""); }} className="text-xs text-rose-500 font-semibold">Remove</button>
              </div>
            ) : (
              <div className="relative">
                <input data-testid="exp-client-search" value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Search client to link" className="mt-1 w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm" />
                {cresults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                    {cresults.map((c) => (
                      <button key={c.id} data-testid={`exp-client-opt-${c.id}`} onClick={() => { setF({ ...f, client_id: c.id }); setClientName(c.full_name); setCq(""); setCresults([]); }} className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-slate-50">{c.full_name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Note (optional)</label>
            <textarea data-testid="exp-note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What was this for?" className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Receipt (optional)</label>
            <label data-testid="exp-receipt-label" className="mt-1 flex items-center gap-2 px-3.5 py-3 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer active:bg-slate-50">
              <Paperclip className="h-4 w-4" />
              {uploading ? "Uploading…" : f.receipt_filename ? <span className="text-slate-800 font-medium truncate">{f.receipt_filename}</span> : "Attach photo or PDF"}
              <input data-testid="exp-receipt-input" type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>

          <button onClick={save} disabled={saving || uploading} data-testid="exp-save" className="w-full bg-primary text-white py-3.5 rounded-xl font-semibold disabled:opacity-60 mt-1">{saving ? "Saving…" : isEdit ? "Save changes" : "Add expense"}</button>
        </div>
      </div>
    </div>
  );
}
